import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import { Layout } from 'antd';
import _ from 'lodash';

import styled from 'styled-components';

import { useLayoutContext } from '@contexts/LayoutContext';
import { ListContext } from '@contexts/ListContext';
import { ProjectProvider, useProjectContext, useProjectContextDispatch } from '@contexts/ProjectContext';
import { useStateValue } from '@contexts/StateProvider';
import { UserContext } from '@contexts/UserContext';

import { useProject } from '@hooks/API/Project/UseProject';
import { forceReloadProjectPackages } from '@hooks/API/Project/UseProjectPackages';
import { forceReloadProjectTimeline } from '@hooks/API/Project/UseProjectTimeline';

import { Loading } from '@components/Gui/Loading';
import StyleVariables from '@components/Gui/StyleVariables';
import ProjectComments from '@components/Project/ProjectComments/ProjectComments';
import ProjectContacts from '@components/Project/ProjectContacts/ProjectContacts';
import ProjectContracts from '@components/Project/ProjectContracts/ProjectContracts';
import ProjectDetailTabs from '@components/Project/ProjectDetailTabs';
import ProjectDocuments from '@components/Project/ProjectDocuments/ProjectDocuments';
import ProjectEventModels from '@components/Project/ProjectEventModels';
import ProjectEvents from '@components/Project/ProjectEvents/ProjectEvents';
import ProjectOverview from '@components/Project/ProjectOverview/ProjectOverview';
import ProjectPageHeader from '@components/Project/ProjectPageHeader';
import ProjectTodos from '@components/Project/ProjectTodos/ProjectTodos';
import ProjectTransactions from '@components/Project/ProjectTransactions/ProjectTransactions';

const StyledLayout = styled(Layout)`
    min-height: calc(100vh - 168px);
    padding: ${(props) => (props.$mobileView ? '8px 0 16px 0' : '8px 16px 16px')};
`;

const ProjectPageContent = ({
    eventId,
    eventType,
    isdrawer,
    onClose,
    projectId,
    selectedProject,
    latestProjectData,
}) => {
    const { breakpoints } = useLayoutContext();
    const [{ areaContext }] = useStateValue();
    const params = useParams();
    const navigate = useNavigate();
    const { currentUser } = useContext(UserContext);
    const { cachedList } = useContext(ListContext);

    const projectContext = useProjectContext();
    const projectDispatch = useProjectContextDispatch();

    const { projectData, selectedEvent, layout } = projectContext;

    // Use ref to track previous projectData and prevent unnecessary updates
    const previousProjectDataRef = useRef();

    // Update context when projectData changes (e.g., after forceReloadProject)
    useEffect(() => {
        // Only update if data actually changed (deep comparison to avoid infinite loops)
        if (latestProjectData && !_.isEqual(previousProjectDataRef.current, latestProjectData)) {
            projectDispatch({ type: 'UpdateProjectData', projectData: latestProjectData });
            previousProjectDataRef.current = latestProjectData;
            forceReloadProjectPackages(projectId);
        }
    }, [latestProjectData, projectDispatch]);

    const selectedEventFromParams = isdrawer ? eventId : Number(params.eventId);
    const selectedEventTypeFromParams = isdrawer ? eventType : params.eventType;

    const handleMoveToEvent = (eventID, eventType) => {
        projectDispatch({ type: 'SetSelectedEvent', eventID, eventType });
        window.scrollTo(0, 0);
        projectDispatch({ type: 'SetLayout', layout: 'calendar' });
    };

    useEffect(() => {
        // Reset when projectId changes
        projectDispatch({ type: 'Reset' });
    }, [projectId]);

    useEffect(() => {
        if (selectedEventFromParams && selectedEventTypeFromParams) {
            handleMoveToEvent(Number(selectedEventFromParams), selectedEventTypeFromParams);
        }
    }, [selectedEventFromParams, selectedEventTypeFromParams]);

    const handleSetLayout = (newLayout) => {
        projectDispatch({ type: 'SetLayout', layout: newLayout });
    };

    const afterProjectDelete = () => {
        if (isdrawer && onClose) {
            onClose();
        } else {
            navigate(`/schedule`);
        }
    };

    const getNetworkAgencyName = (networkAgencyId) => {
        let networkAgencies = [];
        for (const accountingItem of cachedList.accountingGroups.items) {
            networkAgencies = [...networkAgencies, ...accountingItem.NetworkAgencies];
        }
        const networkAgency = networkAgencies.find((agency) => agency.ID === networkAgencyId);
        return networkAgency.Name;
    };

    const tabs = [
        <ProjectOverview onMoveToEvent={handleMoveToEvent} afterProjectDelete={afterProjectDelete} />,
        <ProjectEvents openOnEvent={selectedEvent} onSetLayout={handleSetLayout} />,
        <ProjectEventModels />,
        <ProjectTransactions />,
        <ProjectContacts />,
        <>
            <ProjectDocuments />
            <ProjectContracts />
        </>,
        <ProjectComments />,
        <ProjectTodos />,
    ];

    if (!projectData) {
        return <Loading textBlack paddingTop="5rem" />;
    }

    return projectData.NetworkAgencyCreated !== currentUser.ActiveContext.nwAgId &&
        areaContext.area !== 'accounting' ? (
        <div style={{ padding: '6rem 2rem', textAlign: 'center' }}>
            <h2>
                Please switch to the correct network agency ({getNetworkAgencyName(projectData.NetworkAgencyCreated)})
                to view this project
            </h2>
            <p>
                <a style={{ textDecoration: 'underline', color: StyleVariables.PrimaryColor }} href="/schedule/">
                    Go back to booking chart
                </a>
            </p>
        </div>
    ) : (
        <>
            <ProjectPageHeader isdrawer={isdrawer} />
            <StyledLayout $mobileView={breakpoints < 4}>
                <ProjectDetailTabs isdrawer={isdrawer} tabs={tabs} layout={layout} onSetLayout={handleSetLayout} />
            </StyledLayout>
        </>
    );
};

const ProjectPage = ({ eventId, eventType, isdrawer, onClose, projectId }) => {
    const params = useParams();
    const selectedProject = isdrawer ? projectId : Number(params.projectId);
    const { isLoading, error, data: projectData } = useProject(selectedProject);

    if (isLoading) {
        return <Loading textBlack paddingTop="5rem" />;
    }

    if (error || !projectData) {
        return <div>error</div>;
    }

    return (
        <ProjectProvider initialProjectData={projectData}>
            <ProjectPageContent
                eventId={eventId}
                eventType={eventType}
                isdrawer={isdrawer}
                onClose={onClose}
                projectId={projectId}
                selectedProject={selectedProject}
                latestProjectData={projectData}
            />
        </ProjectProvider>
    );
};

export default ProjectPage;
